import { nanoId } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@utima/ui';
import { useFormContext } from '@utima/ui-informed';
import { Input } from 'informed';
import get from 'lodash.get';
import { CircleHelp } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import {
  useDropzone,
  type Accept,
  type DropzoneOptions,
  type DropzoneProps,
  type DropzoneState,
} from 'react-dropzone';

import { DropZoneContent } from './components/DropZoneContent';
import { PreviewFileItem } from './components/PreviewFileItem';
import type { FileUploadAdapter, SingleUploadingFile } from './fileUploadTypes';
import { SimpleTooltip } from '../tooltips/simple-tooltip';

const SUPPORTED_IMAGE_TYPES: Accept = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/svg': ['.svg'],
};

export interface SingleFileUploadProps extends Omit<DropzoneProps, 'children'> {
  name: string;
  containerClassName?: string;
  dropZoneClassName?: string;
  children?: (dropzone: DropzoneState) => React.ReactNode;
  showErrorMessage?: boolean;
  uploadAdapter: FileUploadAdapter;
  label?: string;
  tooltip?: string;
  showOptional?: boolean;
  autoCloseFileMs?: number;
  previewClassName?: string;
}

function formatPublicUrlToInitialFile(publicUrl: string): SingleUploadingFile {
  const pathname = decodeURIComponent(new URL(publicUrl).pathname);
  const fileName = pathname.split('/').pop();

  if (!fileName) {
    throw new Error('Invalid URL: no file name found');
  }

  const [name, ext] = fileName.split(/\.(?=[^.]+$)/);
  const cleanedUpName = name?.split('-').slice(0, -1).join('--');

  return {
    id: nanoId(),
    file: {
      name: cleanedUpName,
      type: ext,
    } as File,
    publicUrl: publicUrl,
    status: 'initial',
  };
}

export function SingleFileUpload({
  name,
  containerClassName,
  dropZoneClassName,
  children,
  showErrorMessage = true,
  uploadAdapter,
  label,
  tooltip,
  showOptional,
  maxFiles = 1,
  maxSize = 5 * 1024 * 1024,
  accept = SUPPORTED_IMAGE_TYPES,
  autoCloseFileMs = 0,
  previewClassName,
  ...restProps
}: SingleFileUploadProps) {
  const { t } = useLingui();
  const { initialValues } = useFormContext<unknown>();
  const initialPublicUrl = get(initialValues, name) as string | undefined;
  console.log('initialPublicUrl', initialPublicUrl);

  const [fileUploaded, setFileUploaded] = useState<
    SingleUploadingFile | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (initialPublicUrl) {
      const initialFile = formatPublicUrlToInitialFile(initialPublicUrl);

      setFileUploaded({
        id: initialFile.id,
        file: {
          name: initialFile.file?.name ?? '',
          size: initialFile.file?.size || 0,
          type: initialFile.file?.type ?? '',
        } as File,
        status: initialFile.status,
        publicUrl: initialFile.publicUrl,
      });
    }
  }, [initialPublicUrl]);

  const uploadFile = async (file: SingleUploadingFile) => {
    if (!file.file) {
      throw new Error('File cannot be uploaded as it seems to be corrupted');
    }

    try {
      await uploadAdapter.upload(file.file);
      setFileUploaded({
        file: file.file,
        id: nanoId(),
        status: 'completed' as const,
      });
    } catch (error) {
      console.error(error);
      setFileUploaded({
        id: nanoId(),
        file: undefined,
        status: 'error' as const,
        errorMessage: error instanceof Error ? error.message : 'Upload failed',
      });
    }
  };

  const handleFileDrop: DropzoneOptions['onDrop'] = async (
    acceptedFiles,
    fileRejections,
    event,
  ) => {
    if (restProps.onDrop) {
      restProps.onDrop(acceptedFiles, fileRejections, event);
    } else {
      if (acceptedFiles.length > 1) {
        throw new Error('Trying to upload multiple files in SingleFileUpload');
      }

      const newFiles = acceptedFiles.map(file => ({
        id: nanoId(),
        file,
        status: 'uploading' as const,
        progress: 0,
      }));

      if (fileRejections.length > 0) {
        let errorMsg = t`Could not upload ${fileRejections[0].file.name}`;

        if (fileRejections.length > 1) {
          errorMsg =
            errorMsg + t`, and ${fileRejections.length - 1} other files.`;
        }

        setErrorMessage(errorMsg);

        return;
      }

      setErrorMessage('');
      await uploadFile(newFiles[0]);
    }
  };

  const handleCloseUploadedFile = () => {
    setFileUploaded(undefined);
  };

  const deleteUploadedFile = async () => {
    if (!uploadAdapter.delete) {
      return;
    }

    try {
      if (fileUploaded?.publicUrl) {
        await uploadAdapter.delete(fileUploaded.publicUrl);
      } else if (fileUploaded?.file) {
        await uploadAdapter.delete(fileUploaded.file);
      }
    } catch (error) {
      console.error('Failed to delete file:', error);

      return;
    }

    handleCloseUploadedFile();
  };

  const dropzone = useDropzone({
    ...restProps,
    multiple: false,
    onDrop: handleFileDrop,
    maxSize,
    accept,
  });

  return (
    <div
      className={cn(
        'flex flex-col h-full max-h-[300px] md:max-h-[500px]',
        containerClassName,
      )}
    >
      {/* Makes sure the initial values stays in form data */}
      {initialPublicUrl && (
        <Input name={name} type='hidden' defaultValue={initialPublicUrl} />
      )}
      {label && (
        <label
          className='mb-2 inline-flex cursor-pointer items-center gap-1 text-sm font-medium'
          htmlFor={dropzone.getInputProps().name}
        >
          {label}

          {tooltip && (
            <SimpleTooltip title={tooltip}>
              <span className='cursor-help text-sm text-placeholder'>
                <CircleHelp size={14} />
              </span>
            </SimpleTooltip>
          )}

          {showOptional && (
            <span className='text-sm text-placeholder'>(optional)</span>
          )}
        </label>
      )}
      {fileUploaded ? (
        <motion.div
          className={cn(
            'flex w-full flex-col overflow-y-auto',
            previewClassName,
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            key={fileUploaded.id}
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <PreviewFileItem
              preview={fileUploaded.publicUrl}
              file={fileUploaded}
              onDelete={uploadAdapter.delete && deleteUploadedFile}
              onClose={handleCloseUploadedFile}
              autoCloseMs={autoCloseFileMs}
            />
          </motion.div>
        </motion.div>
      ) : (
        <div
          {...dropzone.getRootProps()}
          className={cn(
            'flex flex-col justify-center py-6 items-center w-full border-dashed border border-gray-200 rounded-lg hover:bg-accent hover:text-accent-foreground transition-all select-none cursor-pointer h-16 mt-0',
            dropZoneClassName,
          )}
        >
          <input {...dropzone.getInputProps()} />
          {children ? (
            children(dropzone)
          ) : (
            <DropZoneContent dropzone={dropzone} maxSize={maxSize} />
          )}
          {showErrorMessage && errorMessage && (
            <span className='text-xs text-red-600'>{errorMessage}</span>
          )}
        </div>
      )}
    </div>
  );
}
